/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback, useState, useMemo } from 'react'
import MainLayout from '../../layouts/MainLayout'
import Form from '../../components/forms/Form'
import { useNavigate } from 'react-router-dom'
import '../../assets/styles/custom-styles.css'
import useForm from '../../hooks/useForm'
import useLoader from '../../hooks/useLoader'
import { maintenanceFields } from '../../constants/forms/manutencao/maintenanceFields'
import {
  cleanPayload,
  convertStringsToFloats,
  getMissingRequiredFields,
  normalizeFormErrors,
  setDefaultFieldValues,
  objectToFormData,
} from '../../utils/objectUtils'
import useBaseService from '../../hooks/services/useBaseService'
import { entities } from '../../constants/entities'
import PageHeader from '../../components/PageHeader'
import SectionHeader from '../../components/forms/SectionHeader'
import SimpleBody from '../../components/forms/SimpleBody'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useRemoteSelectOptions from '../../hooks/useRemoteSelectOptions'
import useUrlFilters from '../../hooks/useUrlFilters'
import useNotification from '../../hooks/useNotification'
import { v4 as uuidv4 } from 'uuid'
import ItemListSection from '../../components/forms/ItemListSection'
import useSelectOptions from '../../hooks/useSelectOptions'
import { useMaintenancePageLogic } from './hook/useMaintenancePageLogic'
import { transformFiltersForUrl } from '../../utils/transformFiltersForUrl'
import { useSectionFields } from '../../hooks/useSectionFields'
import { Box, TextField, Typography } from '@mui/material'
import { TechnicianStatus } from '../../enums/technician_status-enum'
import { EquipmentEnum } from '../../enums/equipment-enum'
import useQueryWithLoader from '../../hooks/useQueryWithLoader'
import { useOrgan } from '../../hooks/useOrgan'
import { isIsoCivilDate } from '../../utils/dateUtils'

const CreateMaintenancePage = () => {
  const navigate = useNavigate()
  const { showLoader, hideLoader } = useLoader()

  const { selectedOrgan } = useOrgan()

  const { showNotification } = useNotification()
  const [maintenanceSection, itemsSection] = maintenanceFields

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([maintenanceSection]),
    [maintenanceSection],
    [maintenanceSection]
  )
  const {
    formData: itemFormData,
    handleChange: handleItemChange,
    setFormData: setItemFormData,
  } = useForm(setDefaultFieldValues([itemsSection]), [itemsSection])
  const [fieldsItems, setFieldsItems] = useState(itemsSection.fields)

  const { updateField: updateProps, removeFields } = useSectionFields()

  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)
  const queryClient = useQueryClient()

  const [items, setItems] = useState([])
  const [selectedServiceFile, setSelectedServiceFile] = useState(null)

  const handleItemsChange = useCallback(
    (newItems) => {
      setItems(newItems)
      setFormData((prev) => ({ ...prev, items: newItems }))
    },
    [setFormData]
  )

  const { filters, pagination } = useUrlFilters(
    {
      deleted_at: false,
    },
    'maintenance'
  )

  const maintenanceTypeSelect = useRemoteSelectOptions({
    queryKey: ['maintenanceTypeOptions'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.maintenanceTypes.get,
        params,
        params
      )
      const hiddenTypes = new Set([
        'REVISÃO_MANUTENÇÃO_OTRS',
        'MANUTENÇÃO_CORRETIVA',
        'MANUTENÇÃO_PREVENTIVA',
      ])
      const result = response.result
      if (Array.isArray(result?.data)) {
        return {
          ...result,
          data: result.data.filter(
            (type) => !hiddenTypes.has(type.name?.trim()?.toUpperCase())
          ),
        }
      }
      return Array.isArray(result)
        ? result.filter(
            (type) => !hiddenTypes.has(type.name?.trim()?.toUpperCase())
          )
        : result
    },
    fieldId: 'items.maintenance_type_id',
    formData: itemFormData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (maintenanceType) => ({
      value: maintenanceType.id,
      label: maintenanceType.name,
    }),
  })

  const maintenanceStatusSelect = useRemoteSelectOptions({
    queryKey: ['maintenanceStatusOptions'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.maintenanceStatus.get,
        params,
        params
      )
      const result = response.result
      if (Array.isArray(result?.data)) {
        return {
          ...result,
          data: result.data.filter(
            (status) => status.name?.trim()?.toUpperCase() !== 'EM ANDAMENTO'
          ),
        }
      }
      return Array.isArray(result)
        ? result.filter(
            (status) => status.name?.trim()?.toUpperCase() !== 'EM ANDAMENTO'
          )
        : result
    },
    fieldId: 'items.maintenance_status_id',
    formData: itemFormData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (maintenanceStatus) => ({
      value: maintenanceStatus.id,
      label: maintenanceStatus.name,
    }),
  })

  const { data: technicianStatusOptions } = useQueryWithLoader({
    queryKey: ['technicianStatusOptions'],
    queryFn: async () => {
      return await baseServiceGet(entities.technicianStatus.get, {
        deleted_at: false,
        perPage: 1000,
      }).then((res) => res.result.data)
    },
  })

  const [technicianStatusOptionsState, setTechnicianStatusOptionsState] =
    useState([])

  const technicianStatusSelect = useSelectOptions({
    fieldId: 'items.technician_status_id',
    rawOptions: technicianStatusOptionsState,
    formData,
    isMulti: false,
    mapToOption: (technicianStatus) => ({
      value: technicianStatus.id,
      label: technicianStatus.name,
    }),
  })

  // * Bloqueiar campos dinamicamente, para forçar o usuário a selecionar opções por vez
  useEffect(() => {
    let updatedItemsFields = [...itemsSection.fields]

    // * Se o campo items.maintenance_type_id não tiver preenchido, bloqueie os demais campos
    if (Boolean(maintenanceTypeSelect.selectedValue?.value) === false) {
      updatedItemsFields = updateProps({
        arrayDependente: updatedItemsFields,
        field_id: itemsSection.fields
          .filter((item) => item.id !== 'items.maintenance_type_id')
          .map((field) => field.id),
        updates: {
          disabled: true,
        },
      })

      setFieldsItems(updatedItemsFields)
    }

    // * Se o campo items.maintenance_status_id não tiver preenchido, bloqueie os demais campos
    if (Boolean(maintenanceStatusSelect.selectedValue?.value) === false) {
      updatedItemsFields = updateProps({
        arrayDependente: updatedItemsFields,
        field_id: itemsSection.fields
          .filter(
            (item) =>
              item.id !== 'items.maintenance_type_id' &&
              item.id !== 'items.maintenance_status_id'
          )
          .map((field) => field.id),
        updates: {
          disabled: true,
        },
      })

      setFieldsItems(updatedItemsFields)
    }

    // * Se o campo items.technician_status_id não tiver preenchido, bloqueie os demais campos
    if (Boolean(technicianStatusSelect.selectedValue?.value) === false) {
      updatedItemsFields = updateProps({
        arrayDependente: updatedItemsFields,
        field_id: itemsSection.fields
          .filter(
            (item) =>
              item.id !== 'items.maintenance_type_id' &&
              item.id !== 'items.maintenance_status_id' &&
              item.id !== 'items.technician_status_id' &&
              item.id !== 'items.details'
          )
          .map((field) => field.id),
        updates: {
          disabled: true,
        },
      })

      setFieldsItems(updatedItemsFields)
    }
  }, [maintenanceTypeSelect])

  // Filtrar technicianStatusOptions dinamicamente
  useEffect(() => {
    switch (Number(itemFormData.items.maintenance_status_id?.value)) {
      case 1: // Manutenção Em Andamento
        console.log(
          'Status que vieram da API:',
          technicianStatusOptions.map((opt) => opt.name)
        )
        setTechnicianStatusOptionsState(
          technicianStatusOptions.filter((option) =>
            [
              TechnicianStatus.NOT_RELEASED?.label,
              TechnicianStatus.AWAITING_PARTS?.label,
              TechnicianStatus.SLIGHT_DEFECT?.label,
              TechnicianStatus.ASSESSMENT?.label,
            ].includes(option.name)
          )
        )
        break
      case 2: // Manutenção Concluida
        const findEquipment = equipmentSelect.rawData?.find(
          (eq) => eq?.id === equipmentSelect.selectedValue?.value
        )
        setTechnicianStatusOptionsState(
          technicianStatusOptions.filter((option) =>
            [
              TechnicianStatus.RELEASED?.label,
              TechnicianStatus.WITHOUT_REPAIR?.label,
              TechnicianStatus.WARRANTY?.label,
              TechnicianStatus.SLIGHT_DEFECT?.label,
              // * Permitir USO INTERNO se o equipamento estiver disponível para locação
              ...([EquipmentEnum.AVAILABLE].includes(findEquipment?.status_id)
                ? [TechnicianStatus.INTERNAL_USE?.label]
                : []),
            ].includes(option.name)
          )
        )
        break
      default:
        setTechnicianStatusOptionsState(technicianStatusOptions)
        break
    }
  }, [itemFormData.items.maintenance_status_id, technicianStatusOptions])

  const equipmentSelect = useRemoteSelectOptions({
    queryKey: ['equipmentOptions'],
    fieldId: 'maintenance.equipment_id',
    baseServiceGet,
    entityPath: entities.equipments.get,
    extraFilters: {
      status_id: {
        $in: [
          EquipmentEnum.AVAILABLE,
          EquipmentEnum.RESERVED,
          EquipmentEnum.RENTED,
          EquipmentEnum.INTERNAL_USE,
        ],
      },
      is_completed: { $eq: 1 },
    },
    columnDataBase: ['number', 'name'],
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
    mapToOption: (equipment) => ({
      value: equipment.id,
      label: `${equipment.status?.name} | ${equipment.number} - ${equipment.name}`,
    }),
  })

  const productsItemsSelect = useRemoteSelectOptions({
    queryKey: ['productsItems'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.productsItems.get, params)
      return response.result
    },
    fieldId: 'maintenance.product_item_id',
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${
        item.product?.name
      } ${item.category.name} ${item.condition.name}`,
    }),
  })

  //  Manutenção aplica-se apenas a equipamentos
  const whatToSenderOptionsRaw = useMemo(
    () => [{ id: 'equipment', name: 'Equipamento' }],
    []
  )

  const whatToSenderSelect = useSelectOptions({
    fieldId: 'maintenance.what_to_sender',
    formData,
    rawOptions: whatToSenderOptionsRaw,
    isMulti: false,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    setFormData,
  })

  // Mantém o tipo travado em "Equipamento" (o usuário nunca muda)
  useEffect(() => {
    handleChange('maintenance.what_to_sender', {
      value: 'equipment',
      label: 'Equipamento',
    })
  }, [handleChange])

  const selects = {
    'maintenance.equipment_id': equipmentSelect,
    'maintenance.product_item_id': productsItemsSelect,
    'maintenance.what_to_sender': whatToSenderSelect,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const selectsItems = {
    'items.maintenance_type_id': maintenanceTypeSelect,
    'items.maintenance_status_id': maintenanceStatusSelect,
    'items.technician_status_id': technicianStatusSelect,
  }

  const getSelectMetaItems = useCallback(
    (fieldId) => {
      const selectConfig = selectsItems[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selectsItems]
  )

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const getOptionsItems = useCallback(
    (fieldId) => {
      const selectConfig = selectsItems[fieldId]
      return selectConfig?.options || []
    },
    [selectsItems]
  )

  const getSelectedValueItems = useCallback(
    (fieldId) => {
      const selectConfig = selectsItems[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selectsItems]
  )

  const handleSaveItem = useCallback(
    (itemToEdit) => {
      let updatedItems
      const itemData = convertStringsToFloats(itemFormData.items)

      const missingFields = getMissingRequiredFields({
        itemData,
        fields: itemsSection.fields,
        prefix: 'items',
      })

      if (missingFields.length > 0) {
        showNotification(
          'warning',
          'Preencha todos os campos obrigatórios para insumo'
        )
        return false
      }

      const technicianStatusId = Number(
        itemData.technician_status_id?.value ?? itemData.technician_status_id
      )
      const needsDocument = [
        TechnicianStatus.WARRANTY.value,
        TechnicianStatus.ASSESSMENT.value,
      ].includes(technicianStatusId)
      if (
        technicianStatusId === TechnicianStatus.WARRANTY.value &&
        (!Number.isInteger(Number(itemData.warranty_days)) ||
          Number(itemData.warranty_days) < 1 ||
          Number(itemData.warranty_days) > 3650)
      ) {
        showNotification('warning', 'Informe um prazo de garantia entre 1 e 3650 dias.')
        return false
      }

      if (needsDocument && !selectedServiceFile) {
        showNotification(
          'warning',
          'Anexe um documento para registrar um serviço de laudo ou garantia.'
        )
        return false
      }

      if (itemToEdit) {
        updatedItems = items.map((item) =>
          item.id === itemToEdit.id
            ? { ...item, ...itemData, file: selectedServiceFile ?? item.file }
            : item
        )
      } else {
        updatedItems = [
          ...items,
          {
            id: uuidv4().slice(0, 8),
            ...itemData,
            ...(selectedServiceFile ? { file: selectedServiceFile } : {}),
          },
        ]
      }

      setItems(updatedItems)
      handleItemsChange(updatedItems)
      setSelectedServiceFile(null)
      return true
    },
    [items, itemFormData, handleItemsChange, selectedServiceFile, showNotification]
  )

  // Desabilitar o botão de adicionar item quando o usuário tentar adicionar mais de um Serviço
  // pois a manutenção é um serviço por vez
  const isDisabledAddItem = useMemo(() => {
    return items.length >= 1 || !formData?.maintenance?.what_to_sender?.value
  }, [items, formData])

  const initializeItemForm = useCallback(
    (itemData) => {
      setItemFormData({ items: itemData || {} })
    },
    [setItemFormData]
  )

  const renderFormBody = useCallback(
    ({ fields, formData, handleFieldChange, formErrors }) => {
      const technicianStatusId = Number(
        formData?.items?.technician_status_id?.value ??
          formData?.items?.technician_status_id
      )
      const needsDocument = [
        TechnicianStatus.WARRANTY.value,
        TechnicianStatus.ASSESSMENT.value,
      ].includes(technicianStatusId)
      const warrantyDaysError = normalizeFormErrors(formErrors, {
        removeArrayIndex: true,
      }).items?.warranty_days
      const warrantyDaysErrorMessage = Array.isArray(warrantyDaysError)
        ? warrantyDaysError[0]
        : warrantyDaysError

      return (
        <>
          <SimpleBody
            fields={fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={(fieldId, value) => {
              if (fieldId === 'items.technician_status_id') {
                setSelectedServiceFile(null)
                const selectedStatusId = Number(value?.value ?? value)
                if (selectedStatusId !== TechnicianStatus.WARRANTY.value) {
                  handleItemChange('items.warranty_days', '')
                }
              }
              handleFieldChange(fieldId, value)
            }}
            getOptions={getOptionsItems}
            getSelectedValue={getSelectedValueItems}
            getSelectMeta={getSelectMetaItems}
          />
          {technicianStatusId === TechnicianStatus.WARRANTY.value && (
            <TextField
              fullWidth
              required
              type="number"
              label="Prazo para retorno (dias)"
              placeholder="Ex.: 30"
              value={formData?.items?.warranty_days ?? ''}
              onChange={(event) =>
                handleItemChange('items.warranty_days', event.target.value)
              }
              inputProps={{ min: 1, max: 3650, step: 1 }}
              error={Boolean(warrantyDaysErrorMessage)}
              helperText={
                warrantyDaysErrorMessage ||
                'Informe em quantos dias o equipamento deve retornar da garantia.'
              }
              sx={{ mt: 2 }}
            />
          )}
          {needsDocument && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Anexo obrigatório para serviço de{' '}
                {technicianStatusId === TechnicianStatus.WARRANTY.value
                  ? 'garantia'
                  : 'laudo'}
                .
              </Typography>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(event) =>
                  setSelectedServiceFile(event.target.files?.[0] ?? null)
                }
              />
              {selectedServiceFile && (
                <Typography variant="caption" display="block">
                  {selectedServiceFile.name}
                </Typography>
              )}
            </Box>
          )}
        </>
      )
    },
    [
      formErrors,
      getOptionsItems,
      getSelectedValueItems,
      getSelectMetaItems,
      handleItemChange,
      selectedServiceFile,
    ]
  )

  const getItemDisplayValue = useCallback((item, fieldId) => {
    const fieldName = fieldId.split('.').pop()
    const value = item[fieldName]

    if (value && typeof value === 'object' && 'label' in value) {
      return value?.label
    }

    return value || ''
  }, [])

  const createMaintenance = useMutation({
    mutationFn: async (data) => {
      showLoader()
      const response = await baseServicePost(
        entities.maintenances.create,
        data,
        data
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        hideLoader()
        navigate('/manutencao/')
        queryClient.invalidateQueries(['maintenance', filters, pagination])
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const handleSubmit = async () => {
    try {
      if (!isIsoCivilDate(formData?.maintenance?.maintenance_date)) {
        showNotification('warning', 'Informe uma data válida para a manutenção.')
        return
      }

      const cleanedItems = (formData.items || []).map((item) => {
        const { file, ...itemWithoutFile } = item
        return {
          ...cleanPayload(itemWithoutFile),
          ...(file ? { file } : {}),
        }
      })
      const payload = {
        maintenance: cleanPayload({
          ...formData.maintenance,
          organization_id: selectedOrgan?.id,
        }),
        items: cleanedItems,
      }

      return await createMaintenance.mutateAsync(objectToFormData(payload))
    } catch (error) {
      console.error('Erro ao enviar manutenção:', {
        status: error.status,
        message: error.message,
      })
    }
  }

  const handleBack = useCallback(() => {
    navigate('/manutencao/')
  }, [navigate])

  const { maintenanceFieldsState } = useMaintenancePageLogic(whatToSenderSelect)

  const lockedMaintenanceFields = useMemo(() => {
    if (!Array.isArray(maintenanceFieldsState)) return maintenanceFieldsState
    return maintenanceFieldsState.map((field) =>
      field.id === 'maintenance.what_to_sender'
        ? { ...field, disabled: true }
        : field
    )
  }, [maintenanceFieldsState])

  // Remover campos de forma dinamica
  useEffect(() => {
    let updatedItemFields = [...itemsSection.fields]

    // Se for produto é necessário remover o campo de destino
    if (whatToSenderSelect.selectedValue?.value === 'product') {
      updatedItemFields = removeFields({
        arrayDependente: updatedItemFields,
        field_id: ['items.destination'],
      })
    }
    setFieldsItems(updatedItemFields)
  }, [whatToSenderSelect.selectedValue])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de manutenção"
        showBackButton={true}
        backUrl="/manutencao/"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}>
          {() => (
            <>
              <div key={maintenanceSection.section}>
                <SectionHeader section={maintenanceSection.section} />
                <SimpleBody
                  fields={lockedMaintenanceFields}
                  formErrors={normalizeFormErrors(formErrors)}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>
              <Box
                key={itemsSection.section}
                sx={{ mt: '1rem', display: 'flex', flexDirection: 'column' }}>
                <ItemListSection
                  section={itemsSection.section}
                  fields={itemsSection.fields}
                  fieldsState={fieldsItems}
                  initialData={items}
                  onItemsChange={handleItemsChange}
                  renderFormBody={renderFormBody}
                  getItemDisplayValue={getItemDisplayValue}
                  itemFormData={itemFormData}
                  onItemChange={handleItemChange}
                  itemFormErrors={formErrors}
                  onFormSubmit={handleSaveItem}
                  initializeItemForm={initializeItemForm}
                  isDisabledButton={isDisabledAddItem}
                  showAddButton={false}
                />
              </Box>
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default CreateMaintenancePage
